import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Pagination,
  Skeleton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  ViewList as PaginationIcon,
  ViewStream as InfiniteScrollIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
// The shared client, NOT bare axios: setupInterceptors attaches the bearer
// token to this instance only (api/client.js), and everything under /images
// sits behind the Cognito JWT authorizer. Using the global axios sent every
// request unauthenticated — the list came back empty and uploads 401'd.
import client from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { compressImage } from '../../utils/compressImage';
import { useAuthedObjectUrl } from '../../hooks/useAuthedObjectUrl';

// API base URL
// A plain <img src> carries no Authorization header, so the authorizer 401s
// it. Fetch the bytes through the client and render the blob instead. Its own
// component because the hook can't be called from inside the images .map().
const AuthedCardMedia = ({ filePath, alt }) => {
  const { url, failed } = useAuthedObjectUrl('image', filePath);
  if (failed) {
    return (
      <Box
        sx={{
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.secondary',
          fontSize: 14,
        }}
      >
        Preview unavailable
      </Box>
    );
  }
  if (!url) {
    return <Skeleton variant="rectangular" height={200} />;
  }
  return <CardMedia component="img" height="200" image={url} alt={alt} />;
};

const MediaComponent = ({ wildfireId, formatDate }) => {
  const { isStaff } = useAuth();

  // Image display state
  const [images, setImages] = useState([]);
  const [viewMode, setViewMode] = useState('infinite'); // 'infinite' or 'pagination'
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  const [loadingMoreImages, setLoadingMoreImages] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [message, setMessage] = useState(null);
  const observerRef = useRef(null);

  // Image upload state
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [description, setDescription] = useState('');
  const [isSensitive, setIsSensitive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Fetch images with pagination
  const fetchImages = async (pageNum = 1, reset = true) => {
    try {
      const imagesResponse = await client.get('/images', {
        params: {
          wildfire_id: wildfireId,
          page: pageNum,
          page_size: pageSize
        }
      });

      if (reset) {
        setImages(imagesResponse.data.images);
      } else {
        setImages(prevImages => [...prevImages, ...imagesResponse.data.images]);
      }

      setTotalImages(imagesResponse.data.total);
      setTotalPages(imagesResponse.data.total_pages);
      setHasMore(pageNum < imagesResponse.data.total_pages);

      // Store the message from the API response if available
      if (imagesResponse.data.message) {
        setMessage(imagesResponse.data.message);
      } else {
        setMessage(null);
      }

      return imagesResponse.data;
    } catch (error) {
      console.error('Error fetching wildfire images:', error);
      setImages(reset ? [] : prevImages => prevImages);
      setMessage(null);
      return null;
    }
  };

  // Fetch initial images on component mount
  useEffect(() => {
    fetchImages(1, true);
  }, [wildfireId]);

  // Fetch more images for infinite scroll
  const fetchMoreImages = useCallback(async () => {
    if (!hasMore || loadingMoreImages) return;

    setLoadingMoreImages(true);
    const nextPage = Math.ceil(images.length / pageSize) + 1;
    await fetchImages(nextPage, false);
    setLoadingMoreImages(false);
  }, [hasMore, loadingMoreImages, images.length, pageSize, wildfireId]);

  // Observer callback for infinite scroll
  const lastImageElementRef = useCallback(node => {
    if (loadingMoreImages) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && viewMode === 'infinite') {
        fetchMoreImages();
      }
    });

    if (node) observerRef.current.observe(node);
  }, [loadingMoreImages, hasMore, viewMode, fetchMoreImages]);

  // Handle page change for pagination mode
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchImages(newPage, true);
  };

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      // Reset to first page when switching modes
      setPage(1);
      fetchImages(1, true);
    }
  };

  // Image compression is shared with the wildfire wizard + water sources —
  // see utils/compressImage (WhatsApp-style: caps size, fits a data budget,
  // bakes in EXIF orientation, normalises HEIC → JPEG).

  // Handle file selection
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      try {
        // Show loading state while compressing
        setIsUploading(true);

        // Compress all images
        const compressedResults = await Promise.all(
            files.map(file => compressImage(file))
        );

        // Update state with compressed files
        setSelectedFiles(compressedResults.map(result => result.file));
        setFilePreviews(compressedResults.map(result => ({
          file: result.file,
          preview: result.preview,
          originalSize: result.originalSize,
          compressedSize: result.compressedSize
        })));
      } catch (error) {
        console.error('Error compressing images:', error);
        setUploadError('Failed to process images. Please try again with smaller images.');
      } finally {
        setIsUploading(false);
      }
    }
  };

  // Remove a file from selection
  const handleRemoveFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);

    const newPreviews = [...filePreviews];
    newPreviews.splice(index, 1);
    setFilePreviews(newPreviews);
  };

  // Handle upload dialog open
  const handleOpenUploadDialog = () => {
    setOpenUploadDialog(true);
    setSelectedFiles([]);
    setFilePreviews([]);
    setDescription('');
    setIsSensitive(false);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadProgress(0);
  };

  // Handle upload dialog close
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  // Handle image upload
  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) {
      setUploadError('Please select at least one file to upload');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Upload each file sequentially
      const totalFiles = selectedFiles.length;
      let successCount = 0;

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        // Create form data for this file
        const formData = new FormData();
        formData.append('wildfire_id', wildfireId);
        formData.append('description', description);
        formData.append('is_sensitive', isSensitive);
        formData.append('file', file);

        // Upload image
        await client.post(
            '/images/upload',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              onUploadProgress: (progressEvent) => {
                // Calculate progress for this file
                const fileProgress = (progressEvent.loaded / progressEvent.total);
                // Update overall progress (current file progress + completed files)
                setUploadProgress(((fileProgress + i) / totalFiles) * 100);
              }
            }
        );

        successCount++;
      }

      setUploadSuccess(true);

      // Clear form
      setSelectedFiles([]);
      setFilePreviews([]);
      setDescription('');
      setIsSensitive(false);

      // Refresh images
      setPage(1);
      await fetchImages(1, true);

      // Show success message
      setTimeout(() => {
        setOpenUploadDialog(false);
        setUploadSuccess(false);
      }, 2000);

    } catch (error) {
      console.error('Error uploading images:', error);
      setUploadError('Failed to upload one or more images. Please try again later.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Wildfire Images {totalImages > 0 && `(${totalImages})`}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="image view mode"
              size="small"
              sx={{ mr: 2 }}
          >
            <ToggleButton value="infinite" aria-label="infinite scroll">
              <InfiniteScrollIcon fontSize="small" sx={{ mr: 0.5 }} />
              Infinite
            </ToggleButton>
            <ToggleButton value="pagination" aria-label="pagination">
              <PaginationIcon fontSize="small" sx={{ mr: 0.5 }} />
              Pages
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
              variant="contained"
              color="primary"
              startIcon={<UploadIcon />}
              onClick={handleOpenUploadDialog}
          >
            Upload Image
          </Button>
        </Box>
      </Box>

      {images.length === 0 ? (
          <Typography variant="body1">
            {message || "No images available for this wildfire."}
          </Typography>
      ) : (
          <>
            <Grid container spacing={2}>
              {images.map((image, index) => (
                  <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={image.id}
                      ref={index === images.length - 1 ? lastImageElementRef : null}
                  >
                    <Card>
                      <AuthedCardMedia
                          filePath={image.file_path}
                          alt={`Wildfire image ${image.id}`}
                      />
                      <CardContent>
                        {image.description && (
                            <Typography variant="body2" gutterBottom>
                              {image.description}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Uploaded: {formatDate(image.uploaded_at)}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {image.is_approved ? (
                              <Chip
                                  label="Approved"
                                  color="success"
                                  size="small"
                                  sx={{ mr: 1 }}
                              />
                          ) : (
                              <Chip
                                  label="Pending Approval"
                                  color="warning"
                                  size="small"
                                  sx={{ mr: 1 }}
                              />
                          )}
                          {image.is_sensitive && (
                              <Chip
                                  label="Sensitive Content"
                                  color="error"
                                  size="small"
                              />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
              ))}
            </Grid>

            {/* Loading indicator for infinite scroll */}
            {loadingMoreImages && viewMode === 'infinite' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <CircularProgress size={30} />
                </Box>
            )}

            {/* Pagination controls */}
            {viewMode === 'pagination' && totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                  />
                </Box>
            )}
          </>
      )}

      {/* Image Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload Image
          <IconButton
              aria-label="close"
              onClick={handleCloseUploadDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {uploadSuccess ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Image uploaded successfully!
                {isStaff() ? ' It is now visible.' : ' It will be visible after moderation.'}
              </Alert>
          ) : (
              <>
                {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {uploadError}
                    </Alert>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select Images
                  </Typography>

                  <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      sx={{ mb: 2 }}
                  >
                    Choose Files
                    <input
                        type="file"
                        accept="image/*"
                        hidden
                        multiple
                        onChange={handleFileSelect}
                    />
                  </Button>

                  {selectedFiles.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Selected {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}
                      </Typography>
                  )}

                  {filePreviews.length > 0 && (
                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        {filePreviews.map((item, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                              <Box
                                  sx={{
                                    position: 'relative',
                                    '&:hover .remove-button': {
                                      opacity: 1,
                                    }
                                  }}
                              >
                                <img
                                    src={item.preview}
                                    alt={`Preview ${index + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '120px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    className="remove-button"
                                    onClick={() => handleRemoveFile(index)}
                                    sx={{
                                      position: 'absolute',
                                      top: 4,
                                      right: 4,
                                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                      color: 'white',
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                      }
                                    }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  {item.file.name.length > 20
                                      ? item.file.name.substring(0, 17) + '...'
                                      : item.file.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.originalSize && item.compressedSize ?
                                      `${item.compressedSize} KB (reduced from ${item.originalSize} KB)` :
                                      `${Math.round(item.file.size / 1024)} KB`}
                                </Typography>
                              </Box>
                            </Grid>
                        ))}
                      </Grid>
                  )}
                </Box>

                <TextField
                    fullWidth
                    label="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={3}
                    margin="normal"
                />

                <FormControlLabel
                    control={
                      <Switch
                          checked={isSensitive}
                          onChange={(e) => setIsSensitive(e.target.checked)}
                      />
                    }
                    label="Contains sensitive content"
                    sx={{ mt: 1 }}
                />
              </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseUploadDialog} color="inherit">
            Cancel
          </Button>
          <Button
              onClick={handleUploadImages}
              color="primary"
              variant="contained"
              disabled={isUploading || selectedFiles.length === 0 || uploadSuccess}
              startIcon={isUploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {isUploading ? `Uploading (${Math.round(uploadProgress)}%)` : 'Upload All'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MediaComponent;
